import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { OnboardingRuntime } from './components/onboarding/OnboardingRuntime';
import { ProtectedRoute, SuperAdminRoute, PartnerAdminRoute } from './components/ProtectedRoute';
import { BetaAgentsRoute } from './components/BetaAgentsRoute';
import { BetaVoiceAgentsRoute } from './components/BetaVoiceAgentsRoute';
import { EnrichmentRoute } from './components/EnrichmentRoute';
import { EnrichmentSettingsPage } from './components/settings/enrichment/EnrichmentSettingsPage';
import { AppProvider } from './components/AppProvider';
import { DataCacheProvider } from './contexts/DataCacheContext';
import { CalendarModeProvider } from './contexts/CalendarModeContext';
import { TeamPlanningModeProvider } from './contexts/TeamPlanningModeContext';
import { WarehouseModeProvider } from './contexts/WarehouseModeContext';
import { useAuth } from './lib/auth';
import { useSettingsStore } from './lib/stores/settingsStore';
import { usePermissions } from './lib/hooks/usePermissions';
import { findFirstAccessibleRoute, getResourceForPath } from './lib/utils/findAccessibleRoute';
// activeRoleStore usato internamente da auth.ts per effective values
import { supabase } from './lib/supabase';
import { isRegistrationPending, getRegistrationResumeUrl } from './lib/services/registrationResume';
import * as recordingStorage from './lib/services/recordingStorage';
import { useUploadQueueStore } from './lib/stores/uploadQueueStore';

// PWA Components (small, always needed)
import { UpdateNotification } from './components/pwa/UpdateNotification';
import { MarketingTagsController } from './components/analytics/MarketingTagsController';

// Route pubbliche embedded (no auth, spesso in iframe di terzi): NON registrare il Service Worker.
// Il SW PWA con NetworkFirst + timeout 3s causava form bianchi/errori intermittenti sui siti clienti.
const PUBLIC_EMBED_ROUTE = /^\/(sign|signature|book|f|s|v|tracciabilita|prodotto)(\/|$)|^\/newsletter\/unsubscribe/;
const isPublicEmbedRoute = PUBLIC_EMBED_ROUTE.test(window.location.pathname);

// ─── Lazy Page Imports ──────────────────────────────────────────────────────────
// Ogni pagina viene caricata solo quando l'utente la visita

// Auth (static — entry points)
import { Login } from './pages/auth/Login';
import { AccountDeletion } from './pages/AccountDeletion';
import { Logout } from './pages/auth/Logout';
import { ResetPassword } from './pages/auth/ResetPassword';
import { NotFound } from './pages/NotFound';

// Registration flow (static — entry point)
import { RegisterPage } from './pages/RegisterPage';
import { InviteAcceptPage } from './pages/auth/InviteAcceptPage';
import { VerifyEmailPage } from './pages/register/VerifyEmailPage';
import { PasswordSetupPage } from './pages/register/PasswordSetupPage';
import { ProfileSetupPage } from './pages/register/ProfileSetupPage';
import { CompanyDetailsPage } from './pages/register/CompanyDetailsPage';
import { CompletePage } from './pages/register/CompletePage';
import { DiscoveryPage } from './pages/register/DiscoveryPage';
import { ConnectPage } from './pages/register/ConnectPage';
import { ModulesPage } from './pages/register/ModulesPage';
import { TeamInvitePage } from './pages/register/TeamInvitePage';
import { WelcomeProfilePage } from './pages/welcome/WelcomeProfilePage';
import { WelcomeModulesPage } from './pages/welcome/WelcomeModulesPage';

// Public pages (static — entry points, no auth)
import { SignaturePage } from './pages/SignaturePage';
import { EnhancedSignaturePage } from './pages/EnhancedSignaturePage';

// OAuth callbacks (static — must load immediately)
import { OAuthCallback } from './pages/auth/OAuthCallback';
import { GoogleAuthCallback } from './pages/GoogleAuthCallback';
import { MicrosoftAuthCallback } from './pages/MicrosoftAuthCallback';
import { FattureInCloudCallback } from './pages/FattureInCloudCallback';
import { MetaAdsCallback } from './pages/MetaAdsCallback';

// ─── Lazy: Dashboard & Core ─────────────────────────────────────────────────────
const Dashboard = React.lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const ShopifyLinkPage = React.lazy(() => import('./pages/integrazioni/ShopifyLinkPage'));
const Settings = React.lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const MyTasksPage = React.lazy(() => import('./pages/MyTasksPage').then(m => ({ default: m.MyTasksPage })));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const ContactsPage = React.lazy(() => import('./pages/ContactsPage').then(m => ({ default: m.ContactsPage })));
const TwoFactorSetupPage = React.lazy(() => import('./pages/auth/TwoFactorSetupPage').then(m => ({ default: m.TwoFactorSetupPage })));

// ─── Lazy: CRM ──────────────────────────────────────────────────────────────────
const Clients = React.lazy(() => import('./pages/Clients').then(m => ({ default: m.Clients })));
const CallRecordingPage = React.lazy(() => import('./pages/clients/CallRecordingPage').then(m => ({ default: m.CallRecordingPage })));
const ClientTicketsPage = React.lazy(() => import('./pages/clients/ClientTicketsPage').then(m => ({ default: m.ClientTicketsPage })));
const LeadsPage = React.lazy(() => import('./pages/leads/LeadsPage'));

// ─── Lazy: Projects ─────────────────────────────────────────────────────────────
const ProjectsPage = React.lazy(() => import('./pages/projects/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const ProjectDetailsPage = React.lazy(() => import('./pages/projects/ProjectDetailsPage').then(m => ({ default: m.ProjectDetailsPage })));
const ProjectsGanttPage = React.lazy(() => import('./pages/projects/ProjectsGanttPage').then(m => ({ default: m.ProjectsGanttPage })));

// ─── Lazy: Deals & Services ─────────────────────────────────────────────────────
const DealsPage = React.lazy(() => import('./pages/deals/DealsPage').then(m => ({ default: m.DealsPage })));
const DealActualCostsPage = React.lazy(() => import('./pages/deals/DealActualCostsPage').then(m => ({ default: m.DealActualCostsPage })));
const ServicesPage = React.lazy(() => import('./pages/services/ServicesPage').then(m => ({ default: m.ServicesPage })));
const ServiceCostsPage = React.lazy(() => import('./pages/services/ServiceCostsPage').then(m => ({ default: m.ServiceCostsPage })));
const ServiceTemplateCostsPage = React.lazy(() => import('./pages/services/ServiceTemplateCostsPage').then(m => ({ default: m.ServiceTemplateCostsPage })));
const ServiceTaskTemplatesPage = React.lazy(() => import('./pages/services/ServiceTaskTemplatesPage').then(m => ({ default: m.ServiceTaskTemplatesPage })));
const ServiceCommissionPage = React.lazy(() => import('./pages/services/ServiceCommissionPage').then(m => ({ default: m.ServiceCommissionPage })));
const ServiceEditPage = React.lazy(() => import('./pages/services/ServiceEditPage').then(m => ({ default: m.ServiceEditPage })));

// ─── Lazy: Invoices & Finance ────────────────────────────────────────────────────
const InvoiceQueuePage = React.lazy(() => import('./pages/invoices/InvoiceQueuePage').then(m => ({ default: m.InvoiceQueuePage })));
const InvoiceMatchingPage = React.lazy(() => import('./pages/invoices/InvoiceMatchingPage'));
const FattureInCloudPage = React.lazy(() => import('./pages/FattureInCloudPage').then(m => ({ default: m.FattureInCloudPage })));
const CommissionsPage = React.lazy(() => import('./pages/CommissionsPage').then(m => ({ default: m.CommissionsPage })));
const UserCommissionsPage = React.lazy(() => import('./pages/UserCommissionsPage').then(m => ({ default: m.UserCommissionsPage })));
const PaymentMethodsPage = React.lazy(() => import('./pages/payment-methods/PaymentMethodsPage').then(m => ({ default: m.PaymentMethodsPage })));

// ─── Lazy: Reports & Analytics ───────────────────────────────────────────────────
const ReportsPage = React.lazy(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
/** Vecchi link /analytics/:id -> la stessa dashboard dentro Dashboard. */
function AnalyticsToDashboard() {
  const { pageId } = useParams();
  return <Navigate to={pageId ? `/dashboard?d=${encodeURIComponent(pageId)}` : '/dashboard'} replace />;
}

// ─── Lazy: Communication ─────────────────────────────────────────────────────────
// WhatsApp UI vive nel WhatsAppDrawer (Layout, sempre montato). Niente pagina dedicata.
const GmailPage = React.lazy(() => import('./pages/GmailPage'));
const OutlookPage = React.lazy(() => import('./pages/OutlookPage'));
const NewsletterPage = React.lazy(() => import('./pages/NewsletterPage').then(m => ({ default: m.NewsletterPage })));
const AutomationPage = React.lazy(() => import('./pages/AutomationPage'));
const UnsubscribePage = React.lazy(() => import('./pages/newsletter/UnsubscribePage'));

// ─── Lazy: Documents, Calendar, Wiki, Tickets ────────────────────────────────────
const DocumentsPage = React.lazy(() => import('./pages/DocumentsPage').then(m => ({ default: m.DocumentsPage })));
const CalendarPage = React.lazy(() => import('./pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
const WikiPage = React.lazy(() => import('./pages/wiki/WikiPage').then(m => ({ default: m.WikiPage })));
const TicketsPage = React.lazy(() => import('./pages/tickets/TicketsPage').then(m => ({ default: m.TicketsPage })));
const TicketDetailsPage = React.lazy(() => import('./pages/tickets/TicketDetailsPage').then(m => ({ default: m.TicketDetailsPage })));
const MeetingsPage = React.lazy(() => import('./pages/meetings/MeetingsPage').then(m => ({ default: m.MeetingsPage })));
const RecordingPage = React.lazy(() => import('./pages/meetings/RecordingPage').then(m => ({ default: m.RecordingPage })));

// ─── Lazy: Inventory ─────────────────────────────────────────────────────────────
const InventoryPage = React.lazy(() => import('./pages/inventory/InventoryPage').then(m => ({ default: m.InventoryPage })));
const MagazzinoLayout = React.lazy(() => import('./components/magazzino/MagazzinoLayout'));
const MagazzinoMain = React.lazy(() => import('./components/magazzino/MagazzinoMain'));
const ProductForm = React.lazy(() => import('./components/magazzino/ProductForm'));
const CategoryDetail = React.lazy(() => import('./components/magazzino/CategoryDetail'));
const ProductDetail = React.lazy(() => import('./components/magazzino/ProductDetail'));
const PreparazioneDetail = React.lazy(() => import('./components/magazzino/PreparazioneDetail'));
const ConnectStoreWizard = React.lazy(() => import('./components/magazzino/ecommerce').then(m => ({ default: m.ConnectStoreWizard })));
const StorePage = React.lazy(() => import('./components/magazzino/ecommerce').then(m => ({ default: m.StorePage })));

// ─── Lazy: Booking ───────────────────────────────────────────────────────────────
const BookingDashboardPage = React.lazy(() => import('./pages/booking/BookingDashboardPage').then(m => ({ default: m.BookingDashboardPage })));
const BookingDetailPage = React.lazy(() => import('./pages/booking/BookingDetailPage').then(m => ({ default: m.BookingDetailPage })));
const EventTypesPage = React.lazy(() => import('./pages/booking/EventTypesPage').then(m => ({ default: m.EventTypesPage })));
const EventTypeEditPage = React.lazy(() => import('./pages/booking/EventTypeEditPage').then(m => ({ default: m.EventTypeEditPage })));
const AvailabilityPage = React.lazy(() => import('./pages/booking/AvailabilityPage').then(m => ({ default: m.AvailabilityPage })));
const BookingOrgPage = React.lazy(() => import('./pages/booking-public/BookingOrgPage').then(m => ({ default: m.BookingOrgPage })));
const BookingFlowPage = React.lazy(() => import('./pages/booking-public/BookingFlowPage').then(m => ({ default: m.BookingFlowPage })));
const BookingManagePage = React.lazy(() => import('./pages/booking-public/BookingManagePage').then(m => ({ default: m.BookingManagePage })));

// ─── Lazy: Surveys ───────────────────────────────────────────────────────────────
const SurveysPage = React.lazy(() => import('./pages/surveys/SurveysPage'));
const SurveyBuilderPage = React.lazy(() => import('./pages/surveys/SurveyBuilderPage'));
const SurveyResponsesPage = React.lazy(() => import('./pages/surveys/SurveyResponsesPage'));
const SurveyPublicPage = React.lazy(() => import('./pages/survey-public/SurveyPublicPage'));
const QrTraceabilityPage = React.lazy(() => import('./components/magazzino/traceability/QrTraceabilityPage').then(m => ({ default: m.QrTraceabilityPage })));

// ─── Lazy: Lead Forms ────────────────────────────────────────────────────────────
const LeadFormsPage = React.lazy(() => import('./pages/lead-forms/LeadFormsPage'));
const LeadFormBuilderPage = React.lazy(() => import('./pages/lead-forms/LeadFormBuilderPage'));
const LeadFormDetailPage = React.lazy(() => import('./pages/lead-forms/LeadFormDetailPage'));
const QrCodesPage = React.lazy(() => import('./pages/QrCodesPage'));
const ScreenRecordingsPage = React.lazy(() => import('./pages/ScreenRecordingsPage'));
const PublicRecordingPage = React.lazy(() => import('./pages/PublicRecordingPage'));
const ProductPublicPage = React.lazy(() => import('./pages/product-public/ProductPublicPage'));
const LeadFormPublicPage = React.lazy(() => import('./pages/leadform-public/LeadFormPublicPage'));
const DocumentCollectionPage = React.lazy(() => import('./pages/document-collection/DocumentCollectionPage'));

// ─── Lazy: Ads ───────────────────────────────────────────────────────────────────
const AdsPage = React.lazy(() => import('./pages/ads/AdsPage'));
const CampaignDetailPage = React.lazy(() => import('./pages/ads/CampaignDetailPage').then(m => ({ default: m.CampaignDetailPage })));

// ─── Lazy: Signatures ────────────────────────────────────────────────────────────
const EmailSignaturePage = React.lazy(() => import('./pages/EmailSignaturePage').then(m => ({ default: m.EmailSignaturePage })));
const EmailSignatureListPage = React.lazy(() => import('./pages/EmailSignatureListPage').then(m => ({ default: m.EmailSignatureListPage })));
const EmailSignatureCustomPage = React.lazy(() => import('./pages/EmailSignatureCustomPage').then(m => ({ default: m.EmailSignatureCustomPage })));

// ─── Lazy: Partners & Referrers ──────────────────────────────────────────────────
const PartnersPage = React.lazy(() => import('./pages/partners/PartnersPage').then(m => ({ default: m.PartnersPage })));
// Task 5 "Partner drawer unificato": route /partners/:id ora monta il drawer unificato
// (stesso componente della scheda lead/cliente, in modalità partner) al posto della
// vecchia PartnerDetailsPage dedicata — vedi wrapper PartnerDetailsRoute in
// pages/leads/LeadDetailsPage.tsx (estrae :id da useParams e lo passa come partnerId).
const PartnerDetailsRoute = React.lazy(() => import('./pages/leads/LeadDetailsPage').then(m => ({ default: m.PartnerDetailsRoute })));
const PartnerCommissionsPage = React.lazy(() => import('./pages/partners/PartnerCommissionsPage').then(m => ({ default: m.PartnerCommissionsPage })));
const PartnerTasksPage = React.lazy(() => import('./pages/partners/PartnerTasksPage').then(m => ({ default: m.PartnerTasksPage })));
const ReferrerCommissionsPage = React.lazy(() => import('./pages/referrers/ReferrerCommissionsPage').then(m => ({ default: m.ReferrerCommissionsPage })));

// ─── Lazy: Super Admin ───────────────────────────────────────────────────────────
const OrganizationsPage = React.lazy(() => import('./pages/superadmin/OrganizationsPage').then(m => ({ default: m.OrganizationsPage })));
const OrganizationDetailsPage = React.lazy(() => import('./pages/superadmin/OrganizationDetailsPage').then(m => ({ default: m.OrganizationDetailsPage })));
const SubscriptionPlansPage = React.lazy(() => import('./pages/superadmin/SubscriptionPlansPage').then(m => ({ default: m.SubscriptionPlansPage })));
const WhatsAppWorkerPage = React.lazy(() => import('./pages/superadmin/WhatsAppWorkerPage').then(m => ({ default: m.WhatsAppWorkerPage })));
const AIConfigPage = React.lazy(() => import('./pages/superadmin/AIConfigPage').then(m => ({ default: m.AIConfigPage })));
const AIModelRoutingPage = React.lazy(() => import('./pages/superadmin/AIModelRoutingPage').then(m => ({ default: m.AIModelRoutingPage })));
const AgentsPage = React.lazy(() => import('./pages/agents/AgentsPage').then(m => ({ default: m.AgentsPage })));
const AgentBindingsPage = React.lazy(() => import('./pages/agents/AgentBindingsPage').then(m => ({ default: m.AgentBindingsPage })));
const AgentTemplatesPage = React.lazy(() => import('./pages/agents/AgentTemplatesPage').then(m => ({ default: m.AgentTemplatesPage })));
// Voice Agents (ElevenLabs) — beta, default exports
const VoiceAgentsListPage = React.lazy(() => import('./pages/voice/VoiceAgentsListPage'));
const VoiceCallHistoryPage = React.lazy(() => import('./pages/voice/VoiceCallHistoryPage'));
const VoiceHitlPendingPage = React.lazy(() => import('./pages/voice/VoiceHitlPendingPage'));
const VoiceAgentDetailPage = React.lazy(() => import('./pages/voice/VoiceAgentDetailPage'));
const VoiceAgentBindingsPage = React.lazy(() => import('./pages/voice/VoiceAgentBindingsPage'));
const VoiceUsagePage = React.lazy(() => import('./pages/voice/VoiceUsagePage'));
const VoiceSetupWizardPage = React.lazy(() => import('./pages/settings/VoiceSetupWizardPage'));
const VoicePhoneNumbersPage = React.lazy(() => import('./pages/settings/VoicePhoneNumbersPage'));
const VoiceByokPage = React.lazy(() => import('./pages/settings/VoiceByokPage'));
const GlobalFeatureTogglesPage = React.lazy(() => import('./pages/superadmin/GlobalFeatureTogglesPage').then(m => ({ default: m.GlobalFeatureTogglesPage })));
const OAuthClientsPage = React.lazy(() => import('./pages/superadmin/OAuthClientsPage').then(m => ({ default: m.OAuthClientsPage })));
const CommercialePage = React.lazy(() => import('./pages/superadmin/CommercialePage').then(m => ({ default: m.CommercialePage })));
const TrialDashboardPage = React.lazy(() => import('./pages/superadmin/TrialDashboardPage').then(m => ({ default: m.TrialDashboardPage })));
const CampaignsMonitorPage = React.lazy(() => import('./pages/superadmin/CampaignsMonitorPage').then(m => ({ default: m.CampaignsMonitorPage })));
const WorkflowsOpsPage = React.lazy(() => import('./pages/superadmin/WorkflowsOpsPage').then(m => ({ default: m.WorkflowsOpsPage })));
const UserFeedbackPage = React.lazy(() => import('./pages/superadmin/UserFeedbackPage').then(m => ({ default: m.UserFeedbackPage })));
const SuperadminLogsPage = React.lazy(() => import('./pages/superadmin/SuperadminLogsPage'));
const DpoPanelPage = React.lazy(() => import('./pages/superadmin/DpoPanelPage').then(m => ({ default: m.DpoPanelPage })));
const FattureInCloudFlusiaPage = React.lazy(() => import('./pages/superadmin/FattureInCloudFlusiaPage').then(m => ({ default: m.FattureInCloudFlusiaPage })));
const LockedFeatureLanding = React.lazy(() => import('./pages/LockedFeatureLanding').then(m => ({ default: m.LockedFeatureLanding })));
const PermissionMockupPage = React.lazy(() => import('./pages/superadmin/PermissionMockupPage').then(m => ({ default: m.PermissionMockupPage })));
const CoreStatusPage = React.lazy(() => import('./pages/superadmin/CoreStatusPage').then(m => ({ default: m.CoreStatusPage })));
const EcommerceEnginePage = React.lazy(() => import('./pages/superadmin/EcommerceEnginePage').then(m => ({ default: m.EcommerceEnginePage })));

// ─── Lazy: Partner Admin ─────────────────────────────────────────────────────────
const PartnerAdminDashboard = React.lazy(() => import('./pages/partner-admin/PartnerAdminDashboard').then(m => ({ default: m.PartnerAdminDashboard })));
const PartnerAdminOrgDetails = React.lazy(() => import('./pages/partner-admin/PartnerAdminOrgDetails').then(m => ({ default: m.PartnerAdminOrgDetails })));
const PartnerAdminOpportunities = React.lazy(() => import('./pages/partner-admin/PartnerAdminOpportunities').then(m => ({ default: m.PartnerAdminOpportunities })));
const PartnerAdminOrganizations = React.lazy(() => import('./pages/partner-admin/PartnerAdminOrganizations').then(m => ({ default: m.PartnerAdminOrganizations })));

// ─── Lazy: Portals ───────────────────────────────────────────────────────────────
const PartnerLogin = React.lazy(() => import('./pages/partner-portal/PartnerLogin').then(m => ({ default: m.PartnerLogin })));
const PartnerDashboard = React.lazy(() => import('./pages/partner-portal/PartnerDashboard').then(m => ({ default: m.PartnerDashboard })));
const PartnerServices = React.lazy(() => import('./pages/partner-portal/PartnerServices').then(m => ({ default: m.PartnerServices })));
const PartnerServiceDetail = React.lazy(() => import('./pages/partner-portal/PartnerServiceDetail').then(m => ({ default: m.PartnerServiceDetail })));
const PartnerServiceRequests = React.lazy(() => import('./pages/partner-portal/PartnerServiceRequests').then(m => ({ default: m.PartnerServiceRequests })));
const PartnerAuthGuard = React.lazy(() => import('./components/partner-portal/PartnerAuthGuard').then(m => ({ default: m.PartnerAuthGuard })));
const PartnerPortalLayout = React.lazy(() => import('./components/partner-portal/PartnerPortalLayout').then(m => ({ default: m.PartnerPortalLayout })));

const ClientLogin = React.lazy(() => import('./pages/client-portal/ClientLogin').then(m => ({ default: m.ClientLogin })));
const ClientDashboard = React.lazy(() => import('./pages/client-portal/ClientDashboard').then(m => ({ default: m.ClientDashboard })));
const ClientProjectView = React.lazy(() => import('./pages/client-portal/ClientProjectView').then(m => ({ default: m.ClientProjectView })));
const ClientProfile = React.lazy(() => import('./pages/client-portal/ClientProfile').then(m => ({ default: m.ClientProfile })));
const ClientAuthGuard = React.lazy(() => import('./components/client-portal/ClientAuthGuard').then(m => ({ default: m.ClientAuthGuard })));
const ClientPortalLayout = React.lazy(() => import('./components/client-portal/ClientPortalLayout').then(m => ({ default: m.ClientPortalLayout })));

const CollaboratorLogin = React.lazy(() => import('./pages/collaborator-portal/CollaboratorLogin').then(m => ({ default: m.CollaboratorLogin })));
const CollaboratorDashboard = React.lazy(() => import('./pages/collaborator-portal/CollaboratorDashboard').then(m => ({ default: m.CollaboratorDashboard })));
const CollaboratorProjectView = React.lazy(() => import('./pages/collaborator-portal/CollaboratorProjectView').then(m => ({ default: m.CollaboratorProjectView })));
const CollaboratorTaskDetail = React.lazy(() => import('./pages/collaborator-portal/CollaboratorTaskDetail').then(m => ({ default: m.CollaboratorTaskDetail })));
const CollaboratorAuthGuard = React.lazy(() => import('./components/collaborator-portal/CollaboratorAuthGuard').then(m => ({ default: m.CollaboratorAuthGuard })));
const CollaboratorPortalLayout = React.lazy(() => import('./components/collaborator-portal/CollaboratorPortalLayout').then(m => ({ default: m.CollaboratorPortalLayout })));

// ─── Lazy: Misc ──────────────────────────────────────────────────────────────────
const ResponsiveTest = React.lazy(() => import('./pages/test/ResponsiveTest'));
const ReferralsPage = React.lazy(() => import('./pages/ReferralsPage').then(m => ({ default: m.ReferralsPage })));

// ─── Loading fallback ────────────────────────────────────────────────────────────
function PageLoader() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function RootRedirect() {
  const { isSuperAdmin, isPartnerAdmin, loading } = useAuth();
  const { settings } = useSettingsStore();
  const { hasResourceAccess, hasPermission, loading: permsLoading } = usePermissions();

  if (loading || permsLoading) {
    return <div>Loading...</div>;
  }

  // Usa i valori EFFETTIVI (gia' filtrati per activeRole in auth.ts)
  if (isSuperAdmin) {
    return <Navigate to="/organizations" replace />;
  }

  if (isPartnerAdmin) {
    return <Navigate to="/partner-admin" replace />;
  }

  // settings.default_route: rispettalo solo se l'utente ha accesso a quella rotta,
  // altrimenti finiremmo sulla PermissionDeniedPage come "homepage".
  if (settings?.default_route) {
    const resource = getResourceForPath(settings.default_route);
    if (!resource || hasResourceAccess(resource)) {
      return <Navigate to={settings.default_route} replace />;
    }
  }

  if (hasResourceAccess('dashboard')) {
    return <Navigate to="/dashboard" replace />;
  }

  const fallback = findFirstAccessibleRoute(hasResourceAccess, hasPermission, {
    excludeDashboard: true,
  });
  if (fallback === '/login') {
    // Nessuna rotta accessibile con una sessione attiva: quasi sempre è una
    // registrazione a metà (zero ruoli). Navigare a /login creerebbe un loop
    // (Login vede la sessione e rimanda qui): passa dal gate di resume.
    return <RegistrationResumeGate />;
  }
  return <Navigate to={fallback} replace />;
}

// Gate per sessioni senza alcuna rotta accessibile: se la registrazione è a
// metà riprende il wizard, altrimenti chiude la sessione e mostra il login
// (spezza il loop '/' ↔ '/login' in entrambi i casi).
function RegistrationResumeGate() {
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session && (await isRegistrationPending(session.user.id))) {
          const resumeUrl = await getRegistrationResumeUrl();
          if (resumeUrl) {
            window.location.replace(resumeUrl);
            return;
          }
        }
      } catch (err) {
        console.error('[RegistrationResumeGate] resume check failed:', err);
      }
      await supabase.auth.signOut({ scope: 'local' });
      window.location.replace('/login');
    })();
  }, []);
  return <PageLoader />;
}

// Se l'utente non ha il permesso dashboard_view, lo rimanda a "/" cosi RootRedirect
// sceglie la prima landing accessibile invece di mostrare PermissionDeniedPage.
function DashboardOrRedirect() {
  const { hasResourceAccess, loading } = usePermissions();
  if (loading) return <div>Loading...</div>;
  if (!hasResourceAccess('dashboard')) {
    return <Navigate to="/" replace />;
  }
  return (
    <Layout>
      <Dashboard />
    </Layout>
  );
}

// Backward compat: /whatsapp route redirects to home and opens the drawer.
// Preserva la query string (es. ?tour=whatsapp) cosi il driver.js tour
// puo essere triggerato dopo la redirezione.
function WhatsAppRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  // `chat` e' un JID completo (39333…@c.us, gruppi 1203…-16…@g.us, @lid) e va
  // passato intero; `phone` un numero, solo cifre e almeno 8 (un parametro corto
  // aggancerebbe una chat a caso). `config` dice da quale numero aziendale e'
  // arrivato il messaggio (link delle notifiche).
  const chatParam = (params.get('chat') || '').replace(/[^\w.@-]/g, '');
  const phoneParam = (params.get('phone') || '').replace(/\D/g, '');
  const target = chatParam.includes('@') ? chatParam : phoneParam.length >= 8 ? phoneParam : '';
  const configParam = (params.get('config') || '').replace(/[^\w-]/g, '') || null;
  useEffect(() => {
    // Delay import to avoid circular deps
    import('./lib/stores/whatsappDrawerStore').then(({ useWhatsAppDrawerStore }) => {
      if (target) useWhatsAppDrawerStore.getState().openWithChat(target, configParam);
      else useWhatsAppDrawerStore.getState().open();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Naviga direttamente a /dashboard (target stabile, no redirect chain) preservando ?tour=
  params.delete('chat');
  params.delete('phone');
  params.delete('config');
  const rest = params.toString();
  return <Navigate to={`/dashboard${rest ? `?${rest}` : ''}`} replace />;
}

function NavigateLegacyCampaign() {
  const { campaignId } = useParams();
  return <Navigate to={`/ads/campaigns/${campaignId}`} replace />;
}

/** Effetti globali al boot dell'app (purge OPFS + beforeunload guard upload) */
function AppEffects() {
  useEffect(() => {
    // Purge silenzioso dei pending OPFS più vecchi di 30 giorni
    recordingStorage.purgeOldPending().catch(() => undefined);

    // Blocca navigazione fuori dall'app se c'è un upload attivo
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (useUploadQueueStore.getState().hasActiveUploads()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, []);

  return null;
}

export default function App() {
  return (
    <AppProvider>
      <AppEffects />
      <DataCacheProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
            {/* PWA Components — skippati su route pubbliche embed (form, booking, survey, signature ecc.) */}
            {!isPublicEmbedRoute && (
              <>
                <UpdateNotification />
              </>
            )}

        <TeamPlanningModeProvider>
        <CalendarModeProvider>
        <WarehouseModeProvider>
        <MarketingTagsController />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />
          <Route path="/register/verify" element={<VerifyEmailPage />} />
          <Route path="/register/password" element={<PasswordSetupPage />} />
          <Route path="/register/profile" element={<ProfileSetupPage />} />
          <Route path="/register/company" element={<CompanyDetailsPage />} />
          <Route path="/register/discovery" element={<DiscoveryPage />} />
          <Route path="/register/connect" element={<ConnectPage />} />
          <Route path="/register/modules" element={<ModulesPage />} />
          <Route path="/register/team" element={<TeamInvitePage />} />
          {/* Mini-onboarding utente invitato (post-accept invito) */}
          <Route path="/welcome/profile" element={<WelcomeProfilePage />} />
          <Route path="/welcome/modules" element={<WelcomeModulesPage />} />
          <Route path="/register/complete" element={<CompletePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/account-deletion" element={<AccountDeletion />} />
          <Route path="/auth/callback" element={<OAuthCallback />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/2fa-setup" element={<ProtectedRoute><TwoFactorSetupPage /></ProtectedRoute>} />
          <Route path="/newsletter/unsubscribe" element={<UnsubscribePage />} />

          <Route path="/sign/:id" element={<SignaturePage />} />
          <Route path="/signature/:id" element={<EnhancedSignaturePage />} />

          {/* Public Booking Routes (no auth) */}
          <Route path="/book/:userSlug" element={<BookingOrgPage />} />
          <Route path="/book/:userSlug/:eventTypeSlug" element={<BookingFlowPage />} />
          <Route path="/booking/manage/:publicToken" element={<BookingManagePage />} />

          {/* Public Survey Route (no auth) */}
          <Route path="/s/:surveySlug" element={<SurveyPublicPage />} />

          {/* Public Lead Form Route (no auth) */}
          <Route path="/f/:formSlug" element={<LeadFormPublicPage />} />

          {/* Public Screen Recording Route (no auth) */}
          <Route path="/v/:publicToken" element={<PublicRecordingPage />} />

          {/* Public QR Traceability Route (lot traceability page) */}
          <Route path="/tracciabilita/:lotId" element={<QrTraceabilityPage />} />

          {/* Public Product Page (no auth) — aperta dal QR sull'etichetta prodotto */}
          <Route path="/prodotto/:token" element={<ProductPublicPage />} />

          {/* Raccolta documenti pubblica (no auth) — il collegamento con gettone mandato a chi deve consegnare */}
          <Route path="/documenti/:token" element={<DocumentCollectionPage />} />
          <Route path="/digital-google-connect" element={<GoogleAuthCallback />} />
          <Route path="/digital-microsoft-connect" element={<MicrosoftAuthCallback />} />

          {/* Partner Portal Routes */}
          <Route path="/partner-portal/login" element={<PartnerLogin />} />
          <Route path="/partner-portal" element={
            <PartnerAuthGuard requirePermission="view_services">
              <PartnerPortalLayout>
                <PartnerDashboard />
              </PartnerPortalLayout>
            </PartnerAuthGuard>
          } />
          <Route path="/partner-portal/services" element={
            <PartnerAuthGuard requirePermission="view_services">
              <PartnerPortalLayout>
                <PartnerServices />
              </PartnerPortalLayout>
            </PartnerAuthGuard>
          } />
          <Route path="/partner-portal/services/:serviceId" element={
            <PartnerAuthGuard requirePermission="view_services">
              <PartnerPortalLayout>
                <PartnerServiceDetail />
              </PartnerPortalLayout>
            </PartnerAuthGuard>
          } />
          <Route path="/partner-portal/requests" element={
            <PartnerAuthGuard requirePermission="request_quotes">
              <PartnerPortalLayout>
                <PartnerServiceRequests />
              </PartnerPortalLayout>
            </PartnerAuthGuard>
          } />

          {/* Client Portal Routes */}
          <Route path="/client-portal/login" element={<ClientLogin />} />
          <Route path="/client-portal" element={
            <ClientAuthGuard>
              <ClientPortalLayout>
                <ClientDashboard />
              </ClientPortalLayout>
            </ClientAuthGuard>
          } />
          <Route path="/client-portal/project/:projectId" element={
            <ClientAuthGuard>
              <ClientPortalLayout>
                <ClientProjectView />
              </ClientPortalLayout>
            </ClientAuthGuard>
          } />
          <Route path="/client-portal/profile" element={
            <ClientAuthGuard>
              <ClientPortalLayout>
                <ClientProfile />
              </ClientPortalLayout>
            </ClientAuthGuard>
          } />

          {/* Collaborator Portal Routes */}
          <Route path="/collaborator-portal/login" element={<CollaboratorLogin />} />
          <Route path="/collaborator-portal" element={
            <CollaboratorAuthGuard>
              <CollaboratorPortalLayout>
                <CollaboratorDashboard />
              </CollaboratorPortalLayout>
            </CollaboratorAuthGuard>
          } />
          <Route path="/collaborator-portal/project/:projectId" element={
            <CollaboratorAuthGuard>
              <CollaboratorPortalLayout>
                <CollaboratorProjectView />
              </CollaboratorPortalLayout>
            </CollaboratorAuthGuard>
          } />
          <Route path="/collaborator-portal/project/:projectId/task/:taskId" element={
            <CollaboratorAuthGuard>
              <CollaboratorPortalLayout>
                <CollaboratorTaskDetail />
              </CollaboratorPortalLayout>
            </CollaboratorAuthGuard>
          } />
          <Route path="/collaborator-portal/task/:taskId" element={
            <CollaboratorAuthGuard>
              <CollaboratorPortalLayout>
                <CollaboratorTaskDetail />
              </CollaboratorPortalLayout>
            </CollaboratorAuthGuard>
          } />

          <Route path="/" element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          } />
          <Route path="/integrazioni/shopify/collega" element={
            <ProtectedRoute>
              <ShopifyLinkPage />
            </ProtectedRoute>
          } />
          <Route path="/organizations" element={
            <SuperAdminRoute>
              <Layout>
                <OrganizationsPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/organizations/:organizationId" element={
            <SuperAdminRoute>
              <Layout>
                <OrganizationDetailsPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/subscription-plans" element={
            <SuperAdminRoute>
              <Layout>
                <SubscriptionPlansPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/whatsapp-worker" element={
            <ProtectedRoute>
              <Layout>
                <WhatsAppWorkerPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/ai-config" element={
            <SuperAdminRoute>
              <Layout>
                <AIConfigPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/admin/ai-models" element={
            <SuperAdminRoute>
              <Layout>
                <AIModelRoutingPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/agents" element={
            <BetaAgentsRoute>
              <ProtectedRoute resource="agents" action="view">
                <Layout>
                  <AgentsPage />
                </Layout>
              </ProtectedRoute>
            </BetaAgentsRoute>
          } />
          <Route path="/agents/:agentId/bindings" element={
            <BetaAgentsRoute>
              <ProtectedRoute resource="agents" action="view">
                <Layout>
                  <AgentBindingsPage />
                </Layout>
              </ProtectedRoute>
            </BetaAgentsRoute>
          } />
          <Route path="/agents/templates" element={
            <BetaAgentsRoute>
              <ProtectedRoute resource="agents" action="view">
                <Layout>
                  <AgentTemplatesPage />
                </Layout>
              </ProtectedRoute>
            </BetaAgentsRoute>
          } />
          {/* Voice Agents (ElevenLabs) — beta. /calls PRIMA di /:id per evitare shadowing */}
          <Route path="/agents/voice" element={
            <BetaVoiceAgentsRoute>
              <ProtectedRoute resource="voice_agents" action="view">
                <Layout>
                  <VoiceAgentsListPage />
                </Layout>
              </ProtectedRoute>
            </BetaVoiceAgentsRoute>
          } />
          <Route path="/agents/voice/calls" element={
            <BetaVoiceAgentsRoute>
              <ProtectedRoute resource="voice_agents" action="view">
                <Layout>
                  <VoiceCallHistoryPage />
                </Layout>
              </ProtectedRoute>
            </BetaVoiceAgentsRoute>
          } />
          <Route path="/agents/voice/hitl" element={
            <BetaVoiceAgentsRoute>
              <ProtectedRoute resource="voice_agents" action="dial">
                <Layout>
                  <VoiceHitlPendingPage />
                </Layout>
              </ProtectedRoute>
            </BetaVoiceAgentsRoute>
          } />
          <Route path="/agents/voice/:id" element={
            <BetaVoiceAgentsRoute>
              <ProtectedRoute resource="voice_agents" action="view">
                <Layout>
                  <VoiceAgentDetailPage />
                </Layout>
              </ProtectedRoute>
            </BetaVoiceAgentsRoute>
          } />
          <Route path="/agents/voice/:id/bindings" element={
            <BetaVoiceAgentsRoute>
              <ProtectedRoute resource="voice_agents" action="view">
                <Layout>
                  <VoiceAgentBindingsPage />
                </Layout>
              </ProtectedRoute>
            </BetaVoiceAgentsRoute>
          } />
          <Route path="/settings/voice-setup" element={
            <BetaVoiceAgentsRoute>
              <ProtectedRoute resource="voice_agents" action="manage">
                <Layout>
                  <VoiceSetupWizardPage />
                </Layout>
              </ProtectedRoute>
            </BetaVoiceAgentsRoute>
          } />
          <Route path="/settings/voice-phone-numbers" element={
            <BetaVoiceAgentsRoute>
              <ProtectedRoute resource="voice_agents" action="manage">
                <Layout>
                  <VoicePhoneNumbersPage />
                </Layout>
              </ProtectedRoute>
            </BetaVoiceAgentsRoute>
          } />
          <Route path="/settings/voice-byok" element={
            <BetaVoiceAgentsRoute>
              <ProtectedRoute resource="voice_agents" action="manage">
                <Layout>
                  <VoiceByokPage />
                </Layout>
              </ProtectedRoute>
            </BetaVoiceAgentsRoute>
          } />
          <Route path="/settings/voice-usage" element={
            <BetaVoiceAgentsRoute>
              <ProtectedRoute resource="voice_agents" action="view">
                <Layout>
                  <VoiceUsagePage />
                </Layout>
              </ProtectedRoute>
            </BetaVoiceAgentsRoute>
          } />
          <Route path="/admin/logs" element={
            <SuperAdminRoute>
              <Layout>
                <SuperadminLogsPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/admin/dpo" element={
            <SuperAdminRoute>
              <Layout>
                <DpoPanelPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/admin/fattureincloud-flusia" element={
            <SuperAdminRoute>
              <Layout>
                <FattureInCloudFlusiaPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/unlock/:featureKey" element={
            <ProtectedRoute>
              <Layout>
                <LockedFeatureLanding />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/permission-mockup" element={
            <ProtectedRoute>
              <Layout>
                <PermissionMockupPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/ai-usage" element={<Navigate to="/admin/logs?tab=ai" replace />} />
          <Route path="/admin/feature-toggles" element={
            <SuperAdminRoute>
              <Layout>
                <GlobalFeatureTogglesPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/admin/oauth-clients" element={
            <SuperAdminRoute>
              <Layout>
                <OAuthClientsPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/admin/commerciale" element={
            <SuperAdminRoute>
              <Layout>
                <CommercialePage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/admin/trial-dashboard" element={
            <SuperAdminRoute>
              <Layout>
                <TrialDashboardPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/admin/audit-logs" element={<Navigate to="/admin/logs?tab=audit" replace />} />
          <Route path="/admin/feedback" element={
            <SuperAdminRoute>
              <Layout>
                <UserFeedbackPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/admin/notification-log" element={<Navigate to="/admin/logs?tab=email" replace />} />
          <Route path="/admin/campaigns-monitor" element={
            <SuperAdminRoute>
              <Layout>
                <CampaignsMonitorPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/admin/workflows-ops" element={
            <SuperAdminRoute>
              <Layout>
                <WorkflowsOpsPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/admin/core-status" element={
            <SuperAdminRoute>
              <Layout>
                <CoreStatusPage />
              </Layout>
            </SuperAdminRoute>
          } />
          <Route path="/admin/ecommerce" element={
            <SuperAdminRoute>
              <Layout>
                <EcommerceEnginePage />
              </Layout>
            </SuperAdminRoute>
          } />
          {/* Role chooser rimosso — dual-role users vanno in CRM di default, switch dal dropdown */}
          {/* ─── Partner Admin Routes ─────────────────────────────────── */}
          <Route path="/partner-admin" element={
            <PartnerAdminRoute>
              <Layout>
                <PartnerAdminDashboard />
              </Layout>
            </PartnerAdminRoute>
          } />
          <Route path="/partner-admin/organizations/:organizationId" element={
            <PartnerAdminRoute>
              <Layout>
                <PartnerAdminOrgDetails />
              </Layout>
            </PartnerAdminRoute>
          } />
          <Route path="/partner-admin/organizzazioni" element={
            <PartnerAdminRoute>
              <Layout>
                <PartnerAdminOrganizations />
              </Layout>
            </PartnerAdminRoute>
          } />
          <Route path="/partner-admin/opportunita" element={
            <PartnerAdminRoute>
              <Layout>
                <PartnerAdminOpportunities />
              </Layout>
            </PartnerAdminRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardOrRedirect />
            </ProtectedRoute>
          } />
          <Route path="/clients" element={
            <ProtectedRoute resource="clients" action="view" requiresPermission>
              <Layout>
                <Clients />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/clients/:id" element={
            <ProtectedRoute resource="clients" action="view" requiresPermission>
              <Layout>
                {/* Il dettaglio cliente è un drawer sopra la lista, come lead/offerte/progetti:
                    la route rende Clients, che legge :id e apre il drawer (vedi LeadsPage). */}
                <Clients />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/clients/:id/calls" element={
            <ProtectedRoute resource="clients" action="view" requiresPermission>
              <Layout>
                <CallRecordingPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/clients/:id/tickets" element={
            <ProtectedRoute resource="tickets" action="view" requiresPermission>
              <Layout>
                <ClientTicketsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/leads" element={
            <ProtectedRoute resource="leads" action="view" requiresPermission>
              <Layout>
                <LeadsPage />
              </Layout>
            </ProtectedRoute>
          } />
          {/* Il dettaglio lead è un drawer sopra la lista, come progetti e offerte:
              la route rende LeadsPage, che legge :id e apre il drawer. Così un link
              diretto (es. da una email di notifica) mostra lista + drawer invece
              della pagina intera, e passando da /leads a /leads/:id la lista non
              si rimonta. */}
          <Route path="/leads/:id" element={
            <ProtectedRoute resource="leads" action="view" requiresPermission>
              <Layout>
                <LeadsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/projects" element={
            <ProtectedRoute resource="projects" action="view" requiresPermission>
              <Layout>
                <ProjectsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/projects/gantt" element={
            <ProtectedRoute resource="projects" action="view" requiresPermission>
              <Layout>
                <ProjectsGanttPage />
              </Layout>
            </ProtectedRoute>
          } />
          {/* Il dettaglio progetto è un drawer sopra la lista, come lead e offerte:
              la route rende ProjectsPage, che legge :id e apre il drawer. Così
              passando da /projects a /projects/:id la lista non si rimonta. */}
          <Route path="/projects/:id" element={
            <ProtectedRoute resource="projects" action="view" requiresPermission>
              <Layout>
                <ProjectsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/deals" element={
            <ProtectedRoute resource="deals" action="view" requiresPermission>
              <Layout>
                <DealsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/deals/:id" element={
            <ProtectedRoute resource="deals" action="view" requiresPermission>
              <Layout>
                <DealsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/deals/:dealId/actual-costs" element={
            <ProtectedRoute resource="service_costs" action="view" requiresPermission>
              <Layout>
                <DealActualCostsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/fattureincloud/queue" element={
            <ProtectedRoute resource="fattureincloud" action="view">
              <Layout>
                <InvoiceQueuePage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/invoices/matching" element={
            <ProtectedRoute resource="fattureincloud" action="view">
              <Layout>
                <InvoiceMatchingPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/meetings" element={
            <ProtectedRoute>
              <Layout>
                <MeetingsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/meetings/:id" element={
            <ProtectedRoute>
              <Layout>
                <RecordingPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/tickets" element={
            <ProtectedRoute resource="tickets" action="view" requiresPermission>
              <Layout>
                <TicketsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/tickets/:id" element={
            <ProtectedRoute resource="tickets" action="view" requiresPermission>
              <Layout>
                <TicketDetailsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/wiki" element={
            <ProtectedRoute resource="wiki" action="view" requiresPermission>
              <Layout>
                <WikiPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/wiki/:pageId" element={
            <ProtectedRoute resource="wiki" action="view" requiresPermission>
              <Layout>
                <WikiPage />
              </Layout>
            </ProtectedRoute>
          } />
          {/* Le dashboard della Reportistica stanno nella sezione Dashboard (decisione del 20/10/2026) */}
          <Route path="/analytics" element={<Navigate to="/dashboard" replace />} />
          <Route path="/analytics/:pageId" element={<AnalyticsToDashboard />} />
          <Route path="/reports" element={
            <ProtectedRoute resource="reports" action="view" requiresPermission>
              <Layout>
                <ReportsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/documents" element={
            <ProtectedRoute resource="documents" action="view" requiresPermission>
              <Layout>
                <DocumentsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/calendar" element={
            <ProtectedRoute resource="calendar" action="view" requiresPermission>
              <Layout>
                <CalendarPage />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Booking */}
          <Route path="/bookings" element={
            <ProtectedRoute resource="booking" action="view" requiresPermission>
              <Layout>
                <BookingDashboardPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/bookings/event-types" element={<Navigate to="/bookings?section=event-types" replace />} />
          <Route path="/bookings/event-types/new" element={
            <ProtectedRoute resource="booking" action="manage" requiresPermission>
              <Layout>
                <EventTypeEditPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/bookings/event-types/:id" element={
            <ProtectedRoute resource="booking" action="manage" requiresPermission>
              <Layout>
                <EventTypeEditPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/bookings/availability" element={<Navigate to="/bookings?section=availability" replace />} />
          <Route path="/booking/:id" element={
            <ProtectedRoute resource="booking" action="view" requiresPermission>
              <Layout>
                <BookingDetailPage />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/settings" element={
            <ProtectedRoute>
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/settings/enrichment" element={
            <EnrichmentRoute>
              <ProtectedRoute resource="lead_enrichment" action="view" requiresPermission>
                <Layout>
                  <EnrichmentSettingsPage />
                </Layout>
              </ProtectedRoute>
            </EnrichmentRoute>
          } />
          <Route path="/signature-generator" element={
            <ProtectedRoute resource="email_signature" action="generate" requiresPermission>
              <Layout>
                <EmailSignatureListPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/signature-generator/new" element={
            <ProtectedRoute resource="email_signature" action="generate" requiresPermission>
              <Layout>
                <EmailSignaturePage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/signature-generator/custom/new" element={
            <ProtectedRoute resource="email_signature" action="generate" requiresPermission>
              <Layout>
                <EmailSignatureCustomPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/signature-generator/custom/:id" element={
            <ProtectedRoute resource="email_signature" action="generate" requiresPermission>
              <Layout>
                <EmailSignatureCustomPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/signature-generator/:id" element={
            <ProtectedRoute resource="email_signature" action="generate" requiresPermission>
              <Layout>
                <EmailSignaturePage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/newsletter" element={
            <ProtectedRoute resource="newsletter" action="view" requiresPermission>
              <Layout>
                <NewsletterPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/newsletter/*" element={
            <ProtectedRoute resource="newsletter" action="view" requiresPermission>
              <Layout>
                <NewsletterPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/automation" element={
            <ProtectedRoute resource="settings_automation" action="view" requiresPermission>
              <Layout>
                <AutomationPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/lead-forms" element={
            <ProtectedRoute resource="lead_forms" action="view" requiresPermission>
              <Layout>
                <LeadFormsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/lead-forms/new" element={
            <ProtectedRoute resource="lead_forms" action="manage" requiresPermission>
              <Layout>
                <LeadFormBuilderPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/lead-forms/:id" element={
            <ProtectedRoute resource="lead_forms" action="manage" requiresPermission>
              <Layout>
                <LeadFormBuilderPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/lead-forms/:id/detail" element={
            <ProtectedRoute resource="lead_forms" action="view" requiresPermission>
              <Layout>
                <LeadFormDetailPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/qr-codes" element={
            <ProtectedRoute resource="qr_codes" action="view" requiresPermission>
              <Layout>
                <QrCodesPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/recordings" element={
            <ProtectedRoute resource="screen_recordings" action="view" requiresPermission>
              <Layout>
                <ScreenRecordingsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/surveys" element={
            <ProtectedRoute resource="surveys" action="view" requiresPermission>
              <Layout>
                <SurveysPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/surveys/new" element={
            <ProtectedRoute resource="surveys" action="create" requiresPermission>
              <Layout>
                <SurveyBuilderPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/surveys/:id/edit" element={
            <ProtectedRoute resource="surveys" action="update" requiresPermission>
              <Layout>
                <SurveyBuilderPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/surveys/:id/responses" element={
            <ProtectedRoute resource="surveys" action="view" requiresPermission>
              <Layout>
                <SurveyResponsesPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/my-tasks" element={
            <ProtectedRoute resource="tasks" action="view" requiresPermission>
              <Layout>
                <MyTasksPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/notifications" element={
            <Layout>
              <NotificationsPage />
            </Layout>
          } />
          <Route path="/contacts" element={
            <ProtectedRoute resource="contacts" action="view" requiresPermission>
              <Layout>
                <ContactsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/inventory" element={
            <ProtectedRoute resource="inventory" action="view" requiresPermission>
              <Layout>
                <InventoryPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/magazzino/*" element={
            <ProtectedRoute resource="inventory" action="view" requiresPermission>
              <Layout>
                <MagazzinoLayout />
              </Layout>
            </ProtectedRoute>
          }>
            <Route index element={<MagazzinoMain />} />
            <Route path="prodotti/nuovo" element={<ProductForm mode="create" />} />
            <Route path="prodotti/:id" element={<ProductDetail />} />
            <Route path="prodotti/:id/modifica" element={<ProductForm mode="edit" />} />
            <Route path="categorie/:id" element={<CategoryDetail />} />
            {/* La preparazione ordini vive nella sezione di magazzino: la rotta
                autonoma resta solo per i link vecchi e rimanda li'. */}
            <Route path="preparazione" element={<Navigate to="/magazzino?section=order_preparation" replace />} />
            <Route path="preparazione/:id" element={<PreparazioneDetail />} />
            {/* E-commerce (MD/features/ecommerce/08 sez. 7): collegamento guidato e pagina del negozio */}
            <Route path="negozi/collega" element={<ConnectStoreWizard />} />
            <Route path="negozi/:storeId" element={<StorePage />} />
            <Route path="negozi/:storeId/collega" element={<ConnectStoreWizard />} />
          </Route>
          <Route path="/whatsapp" element={
            <WhatsAppRedirect />
          } />
          <Route path="/gmail" element={
            <Layout>
              <GmailPage />
            </Layout>
          } />
          <Route path="/outlook" element={
            <Layout>
              <OutlookPage />
            </Layout>
          } />
          <Route path="/commissions" element={
            <ProtectedRoute resource="commissions" action="view" requiresPermission>
              <Layout>
                <CommissionsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/commissions/settings" element={
            <ProtectedRoute resource="commissions" action="update" requiresPermission>
              <Layout>
                <UserCommissionsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/services" element={
            <ProtectedRoute resource="services" action="view" requiresPermission>
              <Layout>
                <ServicesPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/services/new" element={
            <ProtectedRoute resource="services" action="create" requiresPermission>
              <Layout>
                <ServiceEditPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/services/:id" element={
            <ProtectedRoute resource="services" action="view" requiresPermission>
              <Layout>
                <ServiceEditPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/services/costs" element={
            <ProtectedRoute resource="client_expenses" action="view" requiresPermission>
              <Layout>
                <ServiceCostsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/services/task-templates" element={
            <ProtectedRoute resource="service_task_templates" action="view" requiresPermission>
              <Layout>
                <ServiceTaskTemplatesPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/services/:serviceId/template-costs" element={
            <ProtectedRoute resource="service_costs" action="view" requiresPermission>
              <Layout>
                <ServiceTemplateCostsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/services/:serviceId/commissions" element={
            <ProtectedRoute resource="commissions" action="view" requiresPermission>
              <Layout>
                <ServiceCommissionPage />
              </Layout>
            </ProtectedRoute>
          } />
          {/* Manteniamo i vecchi percorsi per compatibilità */}
          <Route path="/service-costs" element={<Navigate to="/services/costs" replace />} />
          <Route path="/service-task-templates" element={<Navigate to="/services/task-templates" replace />} />
          <Route path="/payment-methods" element={
            <ProtectedRoute resource="payment_methods" action="view" requiresPermission>
              <Layout>
                <PaymentMethodsPage />
              </Layout>
            </ProtectedRoute>
          } />
          {/* Segnalatori = partner con le provvigioni abilitate (scheda Partner, "Abilita provvigioni"):
              niente sezione separata (decisione di Luca 16/10/2026) */}
          <Route path="/referrers" element={<Navigate to="/partners" replace />} />
          <Route path="/referrers/:id" element={<Navigate to="/partners" replace />} />
          <Route path="/partners" element={
            <ProtectedRoute resource="external_partners" action="view" requiresPermission>
              <Layout>
                <PartnersPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/partners/:id" element={
            <ProtectedRoute resource="external_partners" action="view" requiresPermission>
              <Layout>
                <PartnerDetailsRoute />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/partners/:partnerId/commissions" element={
            <ProtectedRoute resource="external_partners" action="view" requiresPermission>
              <Layout>
                <PartnerCommissionsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/partner-tasks" element={
            <ProtectedRoute resource="partner_tasks" action="view" requiresPermission>
              <Layout>
                <PartnerTasksPage />
              </Layout>
            </ProtectedRoute>
          } />
          {/* Pagina di test componenti responsive */}
          <Route path="/test/responsive" element={
            <ResponsiveTest />
          } />

          <Route path="/referrers/commissions/:referrerId" element={
            <ProtectedRoute resource="referrers" action="view" requiresPermission>
              <Layout>
                <ReferrerCommissionsPage />
              </Layout>
            </ProtectedRoute>
          } />
          {/* Nuove route ADS cockpit */}
          <Route path="/ads" element={
            <ProtectedRoute resource="marketing" action="view" requiresPermission>
              <Layout>
                <AdsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/ads/campaigns/:campaignId" element={
            <ProtectedRoute resource="marketing" action="view" requiresPermission>
              <Layout>
                <CampaignDetailPage />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Redirect legacy /marketing → /ads */}
          <Route path="/marketing" element={<Navigate to="/ads" replace />} />
          <Route path="/marketing/campaigns" element={<Navigate to="/ads" replace />} />
          <Route path="/marketing/campaigns/:campaignId" element={<NavigateLegacyCampaign />} />
          <Route path="/marketing/attribution" element={<Navigate to="/ads" replace />} />
          <Route path="/marketing/simple" element={<Navigate to="/ads" replace />} />
          <Route path="/meta-ads-callback" element={<MetaAdsCallback />} />

          <Route path="/fattureincloud" element={
            <ProtectedRoute resource="fattureincloud" action="view">
              <Layout>
                <FattureInCloudPage />
              </Layout>
            </ProtectedRoute>
          }/>
          <Route path="/fatture-in-cloud" element={<FattureInCloudCallback />} />
          <Route path="/referrals" element={
            <Layout>
              <ReferralsPage />
            </Layout>
          } />
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
          </Suspense>
          {/* Onboarding runtime globale: tour driver.js + setup floating bubble.
              Montato qui (non in Layout) cosi funziona anche su /settings. */}
          <OnboardingRuntime />
          </WarehouseModeProvider>
          </CalendarModeProvider>
          </TeamPlanningModeProvider>
        </Router>
      </DataCacheProvider>
    </AppProvider>
  );
}
